import { useState, useEffect } from 'react';
import { isTestVersionActive, REDESIGN_VERSION_EVENT } from '../services/redesignExperiment';

/**
 * Lightweight read of the redesign experiment's active version. Unlike
 * useRedesignExperiment (which also resolves the member and runs assignment),
 * this just reflects the current `active_version` and re-renders when it
 * changes — ideal for theming shared UI (e.g. page titles) in the new look.
 *
 * `?design=old` / `?design=new` in the URL force which look renders, regardless
 * of the member's bucket/registration date, from the first paint — before
 * useRedesignExperiment has resolved the member and turned the same parameter
 * into a real, pinned switch (see applyDesignOverride). The pin is what keeps
 * the look after the parameter is gone; `?design=reset` drops it. The member's
 * BUCKET is never touched either way. Reads window.location directly rather
 * than react-router's useSearchParams: this hook is also called from App.js
 * itself, above <Router> in the tree, where router hooks aren't available.
 *
 * @returns {boolean} true when the member is viewing the redesigned ("new") version
 */
export default function useIsTestVersion() {
  const [isTest, setIsTest] = useState(() => isTestVersionActive());

  useEffect(() => {
    const handler = () => setIsTest(isTestVersionActive());
    window.addEventListener(REDESIGN_VERSION_EVENT, handler);
    return () => window.removeEventListener(REDESIGN_VERSION_EVENT, handler);
  }, []);

  const designOverride = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('design')
    : null;
  if (designOverride === 'new') return true;
  if (designOverride === 'old') return false;
  return isTest;
}
